import React from 'react';
import { useEvents, useEventDecision } from '../../shared/api/data';
import type { EventState, XcpEvent } from '../../shared/fixtures/types';
import { StatePill, STATE_LABEL } from '../../shared/ui/StatePill';
import { DataClassBadge } from '../../shared/ui/DataClassBadge';
import { useSpineAuthority } from '../../shared/authority/useSpineAuthority';
import '../../app/App.css';

// Rail groups by state, most-actionable first. Matches the design's needs → running → blocked → approved order.
const STATE_ORDER: EventState[] = ['needsrev', 'running', 'blocked', 'approved'];

interface EventsRailProps {
  projectId: string | null;
  onSelectEvent?: (id: string) => void;
}

/**
 * Events rail (C5). Rows → GET /events (role-redacted, authority-gated). Needs-review rows carry a
 * governed sign-off decision (C6, POST /sign-offs) gated on spine-authority signoff:decide.
 */
export function EventsRail({ projectId, onSelectEvent }: EventsRailProps): React.ReactElement {
  const events = useEvents(projectId ?? undefined);
  const decision = useEventDecision();
  const authority = useSpineAuthority();
  const rows = events.data?.data ?? [];
  const grouped = STATE_ORDER.map((s) => ({ state: s, items: rows.filter((e) => e.state === s) })).filter(
    (g) => g.items.length > 0,
  );

  const canDecide = authority.canAct('signoff:decide');
  const decideReason = authority.disabledReason('signoff:decide');

  return (
    <aside className="rail" aria-label="Events">
      <div className="railHead">
        <span className="railTitle">Events</span>
        <DataClassBadge dataClass={events.data?.data_class} />
        <span className="railCount">{rows.length}</span>
      </div>

      {events.isLoading && <div className="stateNote">Loading events…</div>}
      {!events.isLoading && rows.length === 0 && (
        <div className="stateNote">No events for this scope yet.</div>
      )}

      {grouped.map((group) => (
        <section className="railGroup" key={group.state}>
          <h3 className="railGroupHead">
            {STATE_LABEL[group.state]} · {group.items.length}
          </h3>
          {group.items.map((e) => (
            <EventRow
              key={e.id}
              event={e}
              onSelect={onSelectEvent}
              canDecide={canDecide}
              decideReason={decideReason}
              pending={decision.isPending}
              onDecide={(verdict) => decision.mutate({ eventId: e.id, verdict, projectId: projectId ?? undefined })}
            />
          ))}
        </section>
      ))}
    </aside>
  );
}

function EventRow({
  event,
  onSelect,
  canDecide,
  decideReason,
  pending,
  onDecide,
}: {
  event: XcpEvent;
  onSelect?: (id: string) => void;
  canDecide: boolean;
  decideReason?: string;
  pending: boolean;
  onDecide: (verdict: 'approved' | 'rejected') => void;
}): React.ReactElement {
  return (
    <div className="eventRow">
      <button
        type="button"
        className="eventRowMain"
        onClick={() => onSelect?.(event.id)}
        aria-label={`${event.typeLabel}: ${event.title} — ${STATE_LABEL[event.state]}`}
      >
        <div className="eventRowTop">
          <span className="eventType">{event.typeLabel}</span>
          <StatePill state={event.state} />
          <span className="eventWhen">{event.when}</span>
        </div>
        <span className="eventTitle">{event.title}</span>
        <span className="eventBody">{event.body}</span>
      </button>

      {event.type === 'signoff' && event.state === 'needsrev' && (
        <div className="eventDecide">
          <button
            type="button"
            className="decideApprove"
            disabled={!canDecide || pending}
            title={decideReason ? `Disabled: ${decideReason}` : 'Sign off — approve'}
            onClick={() => onDecide('approved')}
          >
            Approve
          </button>
          <button
            type="button"
            className="decideReject"
            disabled={!canDecide || pending}
            title={decideReason ? `Disabled: ${decideReason}` : 'Sign off — reject'}
            onClick={() => onDecide('rejected')}
          >
            Reject
          </button>
        </div>
      )}
    </div>
  );
}
